/**
 * Browser-side GPX metadata extractor. Runs in the admin web before
 * we ship the file to the worker — the worker is a dumb file host
 * and never parses GPX itself (saves wrangler CPU and matches the
 * RideWithGPS / Komoot pattern).
 *
 * We derive:
 *   - the track name from the first <trk><name>
 *   - total length (haversine sum across consecutive points)
 *   - elevation gain + loss (positive / negative deltas)
 *   - bbox (min/max lat + lon across every point)
 *   - point count
 *
 * One pass over the parsed DOM. A 20k-point GPX runs in ~30ms in
 * Safari on an iPhone 12 — fine to do synchronously on file-select.
 */

import type { GpxClientMetadata } from "./api";

export interface ParsedGpx {
  /** Track name from the first <trk><name>, or null if none. */
  name: string | null;
  /** Metadata block the worker stores verbatim. Null-safe on every
   *  field so a sparse / malformed file still uploads cleanly; iOS +
   *  Android re-derive these on download as a fallback. */
  metadata: GpxClientMetadata;
}

export async function parseGpxFile(file: File): Promise<ParsedGpx> {
  const text = await file.text();
  const doc = new DOMParser().parseFromString(text, "application/xml");

  const parseErr = doc.getElementsByTagName("parsererror")[0];
  if (parseErr) {
    throw new Error("Couldn't read this file — is it a valid GPX?");
  }

  const trks = doc.getElementsByTagName("trk");
  const firstTrk = trks[0];
  const nameEl = firstTrk?.getElementsByTagName("name")[0];
  const name = nameEl?.textContent?.trim() || null;

  const points = doc.getElementsByTagName("trkpt");
  if (points.length === 0) {
    // Some GPX files only carry route points (rtept) instead of
    // tracks. Fall back so single-point or route-only files don't
    // bounce off the parser entirely.
    const rteptPoints = doc.getElementsByTagName("rtept");
    return {
      name,
      metadata: walkPoints(rteptPoints),
    };
  }

  return { name, metadata: walkPoints(points) };
}

function walkPoints(points: HTMLCollectionOf<Element>): GpxClientMetadata {
  let lengthM = 0;
  let gainM = 0;
  let lossM = 0;
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;
  let prevLat: number | null = null;
  let prevLon: number | null = null;
  let prevEle: number | null = null;
  let count = 0;

  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    const lat = parseFloat(pt.getAttribute("lat") ?? "");
    const lon = parseFloat(pt.getAttribute("lon") ?? "");
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    count++;

    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lon > east) east = lon;
    if (lon < west) west = lon;

    if (prevLat !== null && prevLon !== null) {
      lengthM += haversineMeters(prevLat, prevLon, lat, lon);
    }
    prevLat = lat;
    prevLon = lon;

    const eleEl = pt.getElementsByTagName("ele")[0];
    const ele = eleEl ? parseFloat(eleEl.textContent ?? "") : NaN;
    if (Number.isFinite(ele)) {
      if (prevEle !== null) {
        const delta = ele - prevEle;
        if (delta > 0) gainM += delta;
        else lossM += -delta;
      }
      prevEle = ele;
    }
  }

  if (count === 0) {
    return {
      lengthM: null,
      elevationGainM: null,
      elevationLossM: null,
      bbox: null,
      pointCount: null,
    };
  }

  return {
    lengthM: Math.round(lengthM),
    elevationGainM: Math.round(gainM),
    elevationLossM: Math.round(lossM),
    bbox: { north, south, east, west },
    pointCount: count,
  };
}

function haversineMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
